import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useAlert } from "react-alert";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/mousewheel";
import { Mousewheel } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  collection,
  doc,
  setDoc,
  addDoc
} from "firebase/firestore";

import { db } from "../../firebase";
import Input from "../Input";
import { ReactComponent as AngleLeft } from "../../images/AngleLeft.svg";
import { CategoriesArray } from "../../utility/data";
import "./AddProduct.scss";
import PosIconModal from "./PosIconModal";
import * as yup from 'yup';

const AddCategory = ({ orgId, selectedCategory, setAddNew, fastpassActivated }) => {
  const { t } = useTranslation("editproduct");
  const userStore = useSelector((state) => state.userReducer);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const [loader, setLoader] = useState(false);
  const [vatIn, setVatIn] = useState("");
  const [vatOut, setVatOut] = useState("");
  const [title, setTitle] = useState("");
  const [hideFromFastpass, setHideFromFastpass] = useState(!fastpassActivated);
  const [selectedColor, setSelectedColor] = useState("");
  const [customIcon, setCustomIcon] = useState(CategoriesArray[0].name);
  
  const categorySchema = yup.object().shape({
    title: yup
      .string()
      .trim()
      .required(t('title_required')),

    vatIn: yup
      .string()
      .required(t('vatIn_required'))
      .test(
        t('vat_range'),
        (value) => {
          const num = Number(value);
          return !isNaN(num) && num >= 0 && num <= 100;
        }
      ),

    vatOut: yup
      .string()
      .required(t('vatOut_required'))
      .test(
        t('vat_range'),
        (value) => {
          const num = Number(value);
          return !isNaN(num) && num >= 0 && num <= 100;
        }
      ),
  });

  useEffect(() => {
    setTitle(selectedCategory?.title || "");
    setSelectedColor(selectedCategory?.ticketColor || "");
    setCustomIcon(selectedCategory?.customIcon || CategoriesArray[0].name);
    setVatIn(selectedCategory?.vatIn || "");
    setVatOut(selectedCategory?.vatOut || "");
  }, [selectedCategory]);

  const alert = useAlert();

  const saveCategory = async () => {
    try {
      await categorySchema.validate({ title, vatIn, vatOut });
      setLoader(true);

      const baseData = {
        title,
        customIcon,
        vatIn,
        vatOut,
        color: selectedColor,
        uid: userStore?.users.uid,
        hideFromFastpass: hideFromFastpass,
        orgId,
      };

      const colRef = collection(db, "Categories");

      try {

        if (Boolean(selectedCategory?.id)) {
          const docRef = doc(colRef, selectedCategory.id);
          await setDoc(docRef, { ...baseData, id: docRef.id }, { merge: true });
        } 
        else {
          const newId = new Date().valueOf().toString();
          const docRef = doc(colRef, newId);
          await setDoc(docRef, { ...baseData, id: newId }, { merge: true });
        }

        alert.show(t("category_created"), { type: "success" });
        setAddNew(false);
      } catch (error) {
        console.error("Error writing document:", error);
        alert.show(t("something_went_wrong"), { type: "error" });
      } finally {
        setLoader(false);
      }
    } 
    catch (validationError) {
      alert.show(validationError.message, { type: "warning" });
      return;
    }
  };

  return (
    <div className="add-product">
      <h4 className="add-product-title" onClick={() => setAddNew(false)}>
        <AngleLeft /> <div className="w-0 flex-grow-1">{t("return")}</div>
      </h4>
      <div className="item">
        <h5 className="subtitle">{t("new_category")}</h5>
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="mb-20">
            <Input
              label={t("title")}
              placeholder={t("category_name")}
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>
          <div className="mb-20">
            <Input
              label={t("vat_in")}
              placeholder={t("enter_vat_in")}
              id="vat_in"
              value={vatIn}
              onChange={(e) => setVatIn(e.target.value)}
              required
              type="number"
            />
          </div>
          <div className="mb-20">
            <Input
              label={t("vat_out")}
              placeholder={t("enter_vat_out")}
              id="vat_out"
              value={vatOut}
              onChange={(e) => setVatOut(e.target.value)}
              required
              maxLength={2}
              type="number"
            />
          </div>
          <div className="switch-container mb-20">
            <label className="switch-container-label">
              {t('hide_fastpass')}
            </label>
            <label className="switch-container-switch">
              <input
                disabled={!fastpassActivated}
                type="checkbox"
                checked={hideFromFastpass}
                onChange={(e) => setHideFromFastpass(e.target.checked)}
              />
              <span className="switch-container-slider"></span>
            </label>
          </div>


          <label className="form-label __form-label">{t("choose_icon")}</label>
          <div className="mb-4">
            <div
              className="productCard max-w-200 mx-auto cursor-pointer"
              onClick={() => setEditModalOpen(true)}
            >
              <span className="color"></span>
              <div className="productImg">
                <img src={CategoriesArray.find((i) => i.name === customIcon).icon} alt="" />
              </div>
            </div>
          </div>
          <button
            type="submit"
            className="themeBtn m-0 w-100 pos-submit-btn"
            onClick={saveCategory}
          >
            {t("save")}
          </button>
        </form>
        <PosIconModal
          modalOpen={editModalOpen}
          setModalOpen={setEditModalOpen}
          customIcon={customIcon}
          setCustomIcon={setCustomIcon}
          selectedCategory={"new"}
        />
      </div>
    </div>
  );
};

const TokenColor = ({
  label,
  positifNumber,
  setPositifNumber,
  numberOfTicket,
  setNumberOfTicket,
  selectedColor,
  setSelectedColor,
}) => {
  const { t } = useTranslation("editproduct");
  const handleColorClick = (color) => {
    if (color === selectedColor) {
      setNumberOfTicket(numberOfTicket + 1);
    } else {
      setSelectedColor(color);
      setNumberOfTicket(1);
    }
  };
  return (
    <>
      {label && <label className="form-label __form-label">{label}</label>}

      <div className="colors-wrapper overflow-hidden">
        <Swiper
          slidesPerView="auto"
          modules={[Mousewheel]}
          spaceBetween={10}
          mousewheel={{
            invert: false,
            forceToAxis: true,
          }}
          direction="horizontal"
        >
          {colors.map((item, index) => (
            <SwiperSlide key={index}>
              <div
                className="color"
                style={{ backgroundColor: item.color }}
                onClick={() => handleColorClick(item.color)}
              >
                {item.color === selectedColor && <span className="counter">{"X"}</span>}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </>
  );
};

const colors = [
  {
    color: "#ed0000",
    id: "1",
  },
  {
    color: "#F44336",
    id: "2",
  },
  {
    color: "#E91E63",
    id: "3",
  },
  {
    color: "#9C27B0",
    id: "4",
  },
  {
    color: "#673AB7",
    id: "5",
  },
  {
    color: "#3F51B5",
    id: "6",
  },
  {
    color: "#2196F3",
    id: "7",
  },
  {
    color: "#03A9F4",
    id: "8",
  },
  {
    color: "#00BCD4",
    id: "9",
  },
  {
    color: "#009688",
    id: "10",
  },
  {
    color: "#4CAF50",
    id: "11",
  },
  {
    color: "#8BC34A",
    id: "12",
  },
  {
    color: "#CDDC39",
    id: "13",
  },
  {
    color: "#FFEB3B",
    id: "14",
  },
  {
    color: "#FFC107",
    id: "15",
  },
  {
    color: "#FF9800",
    id: "16",
  },
  {
    color: "#FF5722",
    id: "17",
  },
  {
    color: "#795548",
    id: "18",
  },
  {
    color: "#9E9E9E",
    id: "19",
  },
  {
    color: "#607D8B",
    id: "20",
  },
  {
    color: "#000000",
    id: "21",
  },
];

export default AddCategory;
